import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Users, FileText, TrendingUp, ShieldCheck, LogOut,
  RefreshCw, Search, X, ChevronLeft, ChevronRight,
  AlertCircle, Clock, Zap, BarChart2, Activity,
  Eye, EyeOff, UserCheck, UserX, ArrowUpRight, DollarSign, XCircle, Mail, MousePointerClick,
} from 'lucide-react';
import {
  adminLogin, saveAdminToken, clearAdminToken, getAdminToken,
  fetchOverview, fetchUsers, fetchCharts, fetchActivity, fetchEarnings, fetchEmailStats, fetchAnalytics,
} from '../services/adminApi';

// ── Helpers ───────────────────────────────────────────────────────────────────

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 2)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const TYPE_LABELS = {
  freelancer:    'Freelancer',
  coach:         'Coach / Consultant',
  gst:           'GST (India)',
  vat:           'VAT Invoice',
  international: 'International',
};

// ── Bar chart ─────────────────────────────────────────────────────────────────

function BarChart({ data = [], color = 'bg-blue-500', label = '', getValue, getLabel, getKey }) {
  const _val   = getValue  || (d => d.count);
  const _label = getLabel  || ((d, v) => `${v} ${label} · ${(d.date || d.month || '').slice(5)}`);
  const _key   = getKey    || (d => d.date || d.month);
  const values = data.map(_val);
  const max    = Math.max(...values, 1);
  const total  = values.reduce((s, v) => s + v, 0);
  return (
    <div>
      <div className="flex items-end gap-px h-24">
        {data.map((d, i) => {
          const v = _val(d);
          return (
            <div key={i} className="flex-1 group relative flex flex-col justify-end" style={{ height: '100%' }}>
              <div
                className={`w-full rounded-sm ${color} opacity-80 group-hover:opacity-100 transition-opacity`}
                style={{ height: `${Math.max((v / max) * 100, v > 0 ? 6 : 1.5)}%` }}
              />
              {v > 0 && (
                <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block z-10 pointer-events-none">
                  <div className="bg-gray-700 text-white text-xs rounded-md px-2 py-1 whitespace-nowrap shadow-lg">
                    {_label(d, v)}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-2 text-xs text-gray-600">
        <span>{(_key(data[0]) || '').slice(5)}</span>
        <span className="text-gray-400 font-medium">{total} total</span>
        <span>{(_key(data[data.length - 1]) || '').slice(5)}</span>
      </div>
    </div>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────

const CARD_COLORS = {
  blue:    { ring: 'bg-blue-500/10',    icon: 'text-blue-400'    },
  amber:   { ring: 'bg-amber-500/10',   icon: 'text-amber-400'   },
  green:   { ring: 'bg-emerald-500/10', icon: 'text-emerald-400' },
  purple:  { ring: 'bg-purple-500/10',  icon: 'text-purple-400'  },
  cyan:    { ring: 'bg-cyan-500/10',    icon: 'text-cyan-400'    },
  rose:    { ring: 'bg-rose-500/10',    icon: 'text-rose-400'    },
  gray:    { ring: 'bg-gray-500/10',    icon: 'text-gray-400'    },
  indigo:  { ring: 'bg-indigo-500/10',  icon: 'text-indigo-400'  },
};

function StatCard({ icon: Icon, label, value, sub, color = 'blue', trend = null }) {
  const c = CARD_COLORS[color] || CARD_COLORS.blue;
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 flex items-start gap-4 hover:border-gray-700 transition-colors">
      <div className={`w-10 h-10 rounded-lg ${c.ring} flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-5 h-5 ${c.icon}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
        <div className="flex items-baseline gap-2 mt-0.5">
          <p className="text-2xl font-bold text-white">{value ?? '—'}</p>
          {trend != null && (
            <span className={`text-xs font-medium flex items-center gap-0.5 ${trend >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              <ArrowUpRight className={`w-3 h-3 ${trend < 0 ? 'rotate-90' : ''}`} />
              {Math.abs(trend)}%
            </span>
          )}
        </div>
        {sub && <p className="text-xs text-gray-600 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// ── Login ─────────────────────────────────────────────────────────────────────

function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState('');
  const [show,     setShow]     = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { token } = await adminLogin(password);
      saveAdminToken(token);
      onLogin(token);
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="relative inline-flex mb-5">
            <div className="w-16 h-16 bg-blue-600/20 border border-blue-500/30 rounded-2xl flex items-center justify-center">
              <ShieldCheck className="w-8 h-8 text-blue-400" />
            </div>
            {/* Glow */}
            <div className="absolute inset-0 rounded-2xl bg-blue-500/10 blur-xl -z-10" />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Admin Access</h1>
          <p className="text-sm text-gray-600 mt-1.5">Invilic · Restricted area</p>
        </div>

        {/* Card */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Admin Password
            </label>
            <div className="relative">
              <input
                type={show ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter your admin secret"
                required
                autoFocus
                className="w-full px-4 py-2.5 pr-10 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
              />
              <button
                type="button"
                onClick={() => setShow(v => !v)}
                tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-400 transition-colors"
              >
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2.5">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={loading || !password}
            className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {loading && <RefreshCw className="w-4 h-4 animate-spin" />}
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────────

const NAV = [
  { id: 'overview',   label: 'Overview',   icon: BarChart2          },
  { id: 'analytics',  label: 'Analytics',  icon: MousePointerClick  },
  { id: 'revenue',    label: 'Revenue',    icon: DollarSign         },
  { id: 'users',      label: 'Users',      icon: Users              },
  { id: 'activity',   label: 'Activity',   icon: Activity           },
  { id: 'emails',     label: 'Emails',     icon: Mail               },
];

function Sidebar({ active, onNav, onLogout }) {
  return (
    <aside className="w-56 flex-shrink-0 bg-gray-950 border-r border-gray-800/60 flex flex-col">
      {/* Brand */}
      <div className="px-5 pt-6 pb-5 border-b border-gray-800/60">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-white leading-none">Invilic</p>
            <p className="text-xs text-gray-600 mt-0.5">Admin Panel</p>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5">
        {NAV.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => onNav(id)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
              active === id
                ? 'bg-blue-600/15 text-blue-400 border border-blue-500/25'
                : 'text-gray-500 hover:text-gray-300 hover:bg-white/4 border border-transparent'
            }`}
          >
            <Icon className="w-4 h-4 flex-shrink-0" />
            {label}
          </button>
        ))}
      </nav>

      {/* Sign out */}
      <div className="px-3 py-4 border-t border-gray-800/60">
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:text-rose-400 hover:bg-rose-500/8 border border-transparent transition-all"
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </button>
      </div>
    </aside>
  );
}

// ── Shared micro-components ───────────────────────────────────────────────────

function PageHeader({ title, children }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
      <h2 className="text-xl font-bold text-white">{title}</h2>
      <div className="flex items-center gap-3">{children}</div>
    </div>
  );
}

function RefreshBtn({ onClick, loading }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 bg-gray-900 border border-gray-800 rounded-lg hover:bg-gray-800 hover:text-gray-300 disabled:opacity-40 transition-all"
    >
      <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
      Refresh
    </button>
  );
}

function SectionLabel({ children }) {
  return (
    <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest mb-3">{children}</p>
  );
}

function Loader() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="flex flex-col items-center gap-3">
        <RefreshCw className="w-6 h-6 text-blue-500 animate-spin" />
        <p className="text-xs text-gray-600">Loading…</p>
      </div>
    </div>
  );
}

function ErrorBanner({ msg, onRetry }) {
  if (msg === 'SESSION_EXPIRED') {
    return (
      <div className="flex items-center gap-3 p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl text-sm text-rose-400">
        <AlertCircle className="w-5 h-5 flex-shrink-0" />
        Session expired — please sign in again.
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl text-sm text-rose-400">
      <AlertCircle className="w-5 h-5 flex-shrink-0" />
      <span className="flex-1">{msg}</span>
      {onRetry && (
        <button onClick={onRetry} className="underline hover:no-underline">Retry</button>
      )}
    </div>
  );
}

// ── Overview tab ──────────────────────────────────────────────────────────────

function OverviewTab({ token }) {
  const now = new Date();
  const [selYear,  setSelYear]  = useState(now.getFullYear());
  const [selMonth, setSelMonth] = useState(now.getMonth() + 1);
  const [overview, setOverview] = useState(null);
  const [charts,   setCharts]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ov, ch] = await Promise.all([
        fetchOverview(token, selYear, selMonth),
        fetchCharts(token),
      ]);
      setOverview(ov);
      setCharts(ch);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, selYear, selMonth]);

  useEffect(() => { load(); }, [load]);

  const yearOptions = [];
  for (let y = 2024; y <= now.getFullYear(); y++) yearOptions.push(y);

  const monthLabel = `${MONTH_NAMES[selMonth - 1]} ${selYear}`;
  const conversionRate = overview?.new_selected_month > 0
    ? Math.round((overview.converted_to_pro_selected_month / overview.new_selected_month) * 100)
    : 0;

  return (
    <div className="p-8">
      <PageHeader title="Overview">
        {/* Month picker */}
        <div className="flex items-center gap-1.5 bg-gray-900 border border-gray-800 rounded-lg px-3 py-1.5">
          <Clock className="w-3.5 h-3.5 text-gray-600" />
          <select
            value={selMonth}
            onChange={e => setSelMonth(Number(e.target.value))}
            className="text-sm text-gray-300 bg-transparent focus:outline-none cursor-pointer"
          >
            {MONTH_NAMES.map((m, i) => (
              <option key={i + 1} value={i + 1} className="bg-gray-900">{m}</option>
            ))}
          </select>
          <select
            value={selYear}
            onChange={e => setSelYear(Number(e.target.value))}
            className="text-sm text-gray-300 bg-transparent focus:outline-none cursor-pointer"
          >
            {yearOptions.map(y => <option key={y} value={y} className="bg-gray-900">{y}</option>)}
          </select>
        </div>
        <RefreshBtn onClick={load} loading={loading} />
      </PageHeader>

      {error && <ErrorBanner msg={error} onRetry={load} />}
      {loading && <Loader />}

      {!loading && overview && (
        <div className="space-y-8">
          {/* All-time stats */}
          <div>
            <SectionLabel>All time</SectionLabel>
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
              <StatCard icon={Users}    label="Total users"    value={overview.total_users}    color="blue" />
              <StatCard icon={Zap}      label="Active Pro"     value={overview.active_pro}     color="amber"
                sub={`$${overview.mrr_estimate?.toLocaleString()} est. MRR`} />
              <StatCard icon={Users}    label="Free users"     value={overview.free_users}     color="gray" />
              <StatCard icon={FileText} label="Total invoices" value={overview.total_invoices} color="indigo" />
            </div>
          </div>

          {/* Month stats */}
          <div>
            <SectionLabel>{monthLabel}</SectionLabel>
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
              <StatCard icon={TrendingUp} label="New signups"        value={overview.new_selected_month}             color="green" />
              <StatCard icon={UserCheck}  label="Converted to Pro"   value={overview.converted_to_pro_selected_month} color="amber"
                sub={overview.new_selected_month > 0 ? `${conversionRate}% conversion rate` : undefined} />
              <StatCard icon={UserX}      label="Stayed on free"     value={overview.stayed_free_selected_month}      color="gray" />
              <StatCard icon={XCircle}    label="Cancellations"      value={overview.cancellations_selected_month ?? 0} color="rose" />
            </div>
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mt-3">
              <StatCard icon={FileText}   label="Invoices created"   value={overview.invoices_selected_month}         color="purple" />
            </div>
          </div>

          {/* Invoice type breakdown */}
          <div>
            <SectionLabel>Invoices by type</SectionLabel>
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">All time</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">{monthLabel}</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-36">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {overview.invoice_by_type.map((row, i) => {
                    const pct = overview.total_invoices
                      ? Math.round((row.all_time / overview.total_invoices) * 100)
                      : 0;
                    return (
                      <tr key={row.type} className={`hover:bg-gray-800/40 transition-colors ${i < overview.invoice_by_type.length - 1 ? 'border-b border-gray-800/60' : ''}`}>
                        <td className="px-5 py-3.5 font-medium text-gray-300">{TYPE_LABELS[row.type] || row.type}</td>
                        <td className="px-5 py-3.5 text-right font-bold text-white">{row.all_time}</td>
                        <td className="px-5 py-3.5 text-right text-gray-400">{row.this_month}</td>
                        <td className="px-5 py-3.5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-20 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                              <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs text-gray-600 w-8 text-right">{pct}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Charts */}
          {charts && (
            <div>
              <SectionLabel>30-day trends</SectionLabel>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">New signups</p>
                  <BarChart data={charts.signups} color="bg-blue-500" label="users" />
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Invoices created</p>
                  <BarChart data={charts.invoices} color="bg-indigo-500" label="invoices" />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Revenue tab ───────────────────────────────────────────────────────────────

function RevenueTab({ token }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchEarnings(token));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const avgPerPayment = data?.total_payment_count > 0
    ? (data.total_revenue_cents / data.total_payment_count / 100).toFixed(2)
    : '0.00';

  return (
    <div className="p-8">
      <PageHeader title="Revenue">
        <RefreshBtn onClick={load} loading={loading} />
      </PageHeader>

      {error && <ErrorBanner msg={error} onRetry={load} />}
      {loading && <Loader />}

      {!loading && data && (
        <div className="space-y-8">
          {/* Top stat cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatCard
              icon={DollarSign}
              label="Total earned"
              value={`$${Number(data.total_revenue_usd).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
              sub={`${data.total_payment_count} payment${data.total_payment_count !== 1 ? 's' : ''} total`}
              color="green"
            />
            <StatCard
              icon={TrendingUp}
              label="This month"
              value={`$${Number(data.this_month_usd).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
              color="blue"
            />
            <StatCard
              icon={Zap}
              label="Avg per payment"
              value={`$${avgPerPayment}`}
              color="amber"
            />
          </div>

          {/* Monthly bar chart */}
          <div>
            <SectionLabel>Monthly revenue (last 13 months)</SectionLabel>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              {data.monthly.every(m => m.revenue_cents === 0) ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-600">
                  <DollarSign className="w-8 h-8 mb-3 opacity-30" />
                  <p className="text-sm">No payments recorded yet</p>
                  <p className="text-xs mt-1 opacity-60">Payments are tracked from subscription.active and subscription.renewed webhooks</p>
                </div>
              ) : (
                <BarChart
                  data={data.monthly}
                  color="bg-emerald-500"
                  getValue={d => d.revenue_cents}
                  getLabel={(d, v) => `$${(v / 100).toFixed(2)} · ${d.month}`}
                  getKey={d => d.month}
                />
              )}
            </div>
          </div>

          {/* Monthly breakdown table */}
          <div>
            <SectionLabel>Monthly breakdown</SectionLabel>
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Month</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Payments</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Revenue</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-36">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {[...data.monthly].reverse().map((row, i) => {
                    const pct = data.total_revenue_cents > 0
                      ? Math.round((row.revenue_cents / data.total_revenue_cents) * 100)
                      : 0;
                    return (
                      <tr key={row.month}
                        className={`hover:bg-gray-800/40 transition-colors ${i < data.monthly.length - 1 ? 'border-b border-gray-800/60' : ''}`}>
                        <td className="px-5 py-3.5 font-medium text-gray-300">{row.month}</td>
                        <td className="px-5 py-3.5 text-right text-gray-400">{row.count}</td>
                        <td className="px-5 py-3.5 text-right font-bold text-white">
                          {row.revenue_cents > 0 ? `$${Number(row.revenue_usd).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—'}
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-20 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                              <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs text-gray-600 w-8 text-right">{pct > 0 ? `${pct}%` : '—'}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Users tab ─────────────────────────────────────────────────────────────────

function UsersTab({ token }) {
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [page,        setPage]        = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [q,           setQ]           = useState('');
  const [planFilter,  setPlanFilter]  = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchUsers(token, page, q, planFilter));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, page, q, planFilter]);

  useEffect(() => { load(); }, [load]);

  function handleSearch(e) {
    e.preventDefault();
    setQ(searchInput);
    setPage(1);
  }

  return (
    <div className="p-8">
      <PageHeader title="Users">
        <RefreshBtn onClick={load} loading={loading} />
      </PageHeader>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search by email…"
              className="pl-9 pr-8 py-2 text-sm bg-gray-900 border border-gray-800 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/40 transition-all w-64"
            />
            {searchInput && (
              <button type="button" onClick={() => { setSearchInput(''); setQ(''); setPage(1); }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-400">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <button type="submit"
            className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors">
            Search
          </button>
        </form>

        <select
          value={planFilter}
          onChange={e => { setPlanFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 text-sm bg-gray-900 border border-gray-800 rounded-lg text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
        >
          <option value="" className="bg-gray-900">All plans</option>
          <option value="pro" className="bg-gray-900">Pro only</option>
          <option value="free" className="bg-gray-900">Free only</option>
        </select>
      </div>

      {error && <ErrorBanner msg={error} onRetry={load} />}

      {loading ? <Loader /> : data && (
        <div className="space-y-4">
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Plan</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Joined</th>
                  <th className="px-5 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Invoices</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Pro expires</th>
                </tr>
              </thead>
              <tbody>
                {data.users.map((u, i) => (
                  <tr key={u.id}
                    className={`hover:bg-gray-800/40 transition-colors ${i < data.users.length - 1 ? 'border-b border-gray-800/60' : ''}`}>
                    <td className="px-5 py-3.5 font-medium text-gray-200">{u.email}</td>
                    <td className="px-5 py-3.5">
                      {u.plan === 'pro' ? (
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${
                          u.is_active_pro
                            ? 'bg-amber-500/15 text-amber-400 border border-amber-500/25'
                            : 'bg-gray-700/50 text-gray-500 border border-gray-700'
                        }`}>
                          <Zap className="w-3 h-3" />
                          {u.is_active_pro ? 'Pro' : 'Expired'}
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-800 text-gray-500 border border-gray-700">
                          Free
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-gray-500">{formatDate(u.created_at)}</td>
                    <td className="px-5 py-3.5 text-center">
                      <span className="font-bold text-white">{u.invoice_count}</span>
                    </td>
                    <td className="px-5 py-3.5 text-gray-600 text-xs">
                      {u.plan_expires_at ? formatDate(u.plan_expires_at) : '—'}
                    </td>
                  </tr>
                ))}
                {data.users.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-16 text-center text-gray-600">No users found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>{data.total} user{data.total !== 1 ? 's' : ''}</span>
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Page {data.page} of {data.pages}</span>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="p-1.5 bg-gray-900 border border-gray-800 rounded-lg hover:bg-gray-800 disabled:opacity-30 transition-colors">
                <ChevronLeft className="w-4 h-4 text-gray-400" />
              </button>
              <button onClick={() => setPage(p => Math.min(data.pages, p + 1))} disabled={page >= data.pages}
                className="p-1.5 bg-gray-900 border border-gray-800 rounded-lg hover:bg-gray-800 disabled:opacity-30 transition-colors">
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Activity tab ──────────────────────────────────────────────────────────────

const STATUS_PILL = {
  finalized: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
  completed: 'bg-blue-500/15 text-blue-400 border-blue-500/25',
  imported:  'bg-amber-500/15 text-amber-400 border-amber-500/25',
};

function ActivityTab({ token }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchActivity(token));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-8"><Loader /></div>;

  return (
    <div className="p-8">
      <PageHeader title="Recent Activity">
        <RefreshBtn onClick={load} loading={loading} />
      </PageHeader>

      {error && <ErrorBanner msg={error} onRetry={load} />}

      {data && (
        <div className="space-y-5">

          {/* Row 1: Latest signups + Recent cancellations */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Latest signups */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-800 flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Users className="w-3.5 h-3.5 text-blue-400" />
                </div>
                <h3 className="text-sm font-semibold text-gray-300">Latest signups</h3>
              </div>
              <ul>
                {data.recent_users.map((u, i) => (
                  <li key={u.id}
                    className={`px-5 py-3.5 flex items-center justify-between gap-3 hover:bg-gray-800/40 transition-colors ${i < data.recent_users.length - 1 ? 'border-b border-gray-800/60' : ''}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-200 truncate">{u.email}</p>
                      <p className="text-xs text-gray-600 mt-0.5">{timeAgo(u.created_at)}</p>
                    </div>
                    {u.plan === 'pro' ? (
                      <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/15 text-amber-400 border border-amber-500/25 text-xs font-semibold rounded-full">
                        <Zap className="w-3 h-3" /> Pro
                      </span>
                    ) : (
                      <span className="flex-shrink-0 px-2 py-0.5 bg-gray-800 text-gray-600 border border-gray-700 text-xs rounded-full">Free</span>
                    )}
                  </li>
                ))}
                {data.recent_users.length === 0 && (
                  <li className="px-5 py-12 text-center text-sm text-gray-600">No signups yet</li>
                )}
              </ul>
            </div>

            {/* Recent cancellations */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-800 flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-rose-500/10 flex items-center justify-center">
                  <XCircle className="w-3.5 h-3.5 text-rose-400" />
                </div>
                <h3 className="text-sm font-semibold text-gray-300">Recent cancellations</h3>
                <span className="ml-auto text-xs text-gray-600">{data.recent_cancellations?.length ?? 0} shown</span>
              </div>
              {(!data.recent_cancellations || data.recent_cancellations.length === 0) ? (
                <div className="px-5 py-12 text-center text-sm text-gray-600">No cancellations yet</div>
              ) : (
                <ul>
                  {data.recent_cancellations.map((c, i) => (
                    <li key={c.id}
                      className={`px-5 py-3.5 hover:bg-gray-800/40 transition-colors ${i < data.recent_cancellations.length - 1 ? 'border-b border-gray-800/60' : ''}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-200 truncate">{c.email}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="inline-flex px-1.5 py-0.5 rounded text-xs font-medium bg-gray-800 text-gray-500 border border-gray-700 capitalize">
                              {c.plan_interval || '—'}
                            </span>
                            <span className="text-xs text-gray-600">{timeAgo(c.plan_cancelled_at)}</span>
                          </div>
                          {c.cancellation_reason && (
                            <p className="text-xs text-gray-500 mt-1 truncate" title={c.cancellation_reason}>
                              "{c.cancellation_reason}"
                            </p>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Row 2: Latest invoices (full width) */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-800 flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                <FileText className="w-3.5 h-3.5 text-indigo-400" />
              </div>
              <h3 className="text-sm font-semibold text-gray-300">Latest invoices</h3>
            </div>
            <ul>
              {data.recent_invoices.map((inv, i) => (
                <li key={inv.id}
                  className={`px-5 py-3.5 flex items-center justify-between gap-3 hover:bg-gray-800/40 transition-colors ${i < data.recent_invoices.length - 1 ? 'border-b border-gray-800/60' : ''}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-200 truncate">{inv.invoice_number}</p>
                    <p className="text-xs text-gray-600 mt-0.5 truncate">{inv.tenant_email} · {timeAgo(inv.created_at)}</p>
                  </div>
                  <span className={`flex-shrink-0 px-2 py-0.5 text-xs font-semibold rounded-full border ${STATUS_PILL[inv.status] || 'bg-gray-800 text-gray-500 border-gray-700'}`}>
                    {inv.status}
                  </span>
                </li>
              ))}
              {data.recent_invoices.length === 0 && (
                <li className="px-5 py-12 text-center text-sm text-gray-600">No invoices yet</li>
              )}
            </ul>
          </div>

        </div>
      )}
    </div>
  );
}

// ── Analytics tab ─────────────────────────────────────────────────────────────

const PATH_LABELS = {
  '/':                              'Homepage',
  '/free-invoice-generator':        'Free Invoice Generator',
  '/gst-invoice-generator':         'GST Invoice Generator',
  '/freelancer-invoice-generator':  'Freelancer Invoice Generator',
  '/vat-invoice-generator':         'VAT Invoice Generator',
  '/invoice-generator-india':       'Invoice Generator India',
  '/international-invoice-generator': 'International Invoice Generator',
  '/coach-invoice-generator':       'Coach Invoice Generator',
  '/gst-invoice-template':          'GST Invoice Template',
  '/freelancer-invoice-template':   'Freelancer Invoice Template',
  '/invoice-template-india':        'Invoice Template India',
  '/service-invoice-template':      'Service Invoice Template',
  '/coach-invoice-template':        'Coach Invoice Template',
  '/vat-invoice-template':          'VAT Invoice Template',
  '/self-employed-invoice-template':'Self-Employed Invoice Template',
  '/contractor-invoice-template':   'Contractor Invoice Template',
  '/invoice-generator-uk':          'Invoice Generator UK',
  '/invoice-generator-australia':   'Invoice Generator Australia',
  '/invoice-generator-us':          'Invoice Generator US',
  '/invoice-generator-canada':      'Invoice Generator Canada',
  '/invoice-generator-uae':         'Invoice Generator UAE',
  '/invoice-generator-singapore':   'Invoice Generator Singapore',
};

function AnalyticsTab({ token }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [days,    setDays]    = useState(30);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchAnalytics(token, days));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, days]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-8">
      <PageHeader title="Page Analytics">
        <div className="flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-lg px-3 py-1.5">
          <Clock className="w-3.5 h-3.5 text-gray-600" />
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            className="text-sm text-gray-300 bg-transparent focus:outline-none cursor-pointer"
          >
            <option value={7}  className="bg-gray-900">Last 7 days</option>
            <option value={30} className="bg-gray-900">Last 30 days</option>
            <option value={90} className="bg-gray-900">Last 90 days</option>
          </select>
        </div>
        <RefreshBtn onClick={load} loading={loading} />
      </PageHeader>

      {error && <ErrorBanner msg={error} onRetry={load} />}
      {loading && <Loader />}

      {!loading && data && (
        <div className="space-y-8">

          {/* Summary stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <StatCard icon={MousePointerClick} label="Total views" value={data.total_views.toLocaleString()} color="blue" />
            <StatCard icon={TrendingUp}        label="Today"       value={data.today_total.toLocaleString()} color="green" />
            <StatCard icon={FileText}          label="Pages tracked" value={data.top_pages.length} color="purple" />
          </div>

          {/* Daily totals chart */}
          <div>
            <SectionLabel>Daily page views — last {days} days</SectionLabel>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              {data.total_views === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-600">
                  <MousePointerClick className="w-8 h-8 mb-3 opacity-30" />
                  <p className="text-sm">No page views recorded yet</p>
                  <p className="text-xs mt-1 opacity-60">Views will appear here once visitors land on your public pages</p>
                </div>
              ) : (
                <BarChart
                  data={data.daily_series}
                  color="bg-blue-500"
                  label="views"
                />
              )}
            </div>
          </div>

          {/* Per-page breakdown table */}
          {data.top_pages.length > 0 && (
            <div>
              <SectionLabel>Pages — click a row to see daily breakdown</SectionLabel>
              <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">#</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Page</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Views</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-40">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.page_breakdown.map((page, i) => {
                      const pct = data.total_views > 0
                        ? Math.round((page.total / data.total_views) * 100)
                        : 0;
                      const isOpen = expanded === page.path;
                      return (
                        <>
                          <tr
                            key={page.path}
                            onClick={() => setExpanded(isOpen ? null : page.path)}
                            className={`cursor-pointer hover:bg-gray-800/40 transition-colors ${
                              i < data.page_breakdown.length - 1 && !isOpen ? 'border-b border-gray-800/60' : ''
                            }`}
                          >
                            <td className="px-5 py-3.5 text-gray-600 text-xs w-8">{i + 1}</td>
                            <td className="px-5 py-3.5">
                              <p className="font-medium text-gray-200 text-sm">
                                {PATH_LABELS[page.path] || page.path}
                              </p>
                              <p className="text-xs text-gray-600 mt-0.5">{page.path}</p>
                            </td>
                            <td className="px-5 py-3.5 text-right font-bold text-white">
                              {page.total.toLocaleString()}
                            </td>
                            <td className="px-5 py-3.5 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-20 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                                  <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                                </div>
                                <span className="text-xs text-gray-600 w-8 text-right">{pct}%</span>
                              </div>
                            </td>
                          </tr>
                          {isOpen && (
                            <tr key={`${page.path}-chart`} className="border-b border-gray-800/60 bg-gray-950/50">
                              <td colSpan={4} className="px-5 py-4">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                                  Daily views — {PATH_LABELS[page.path] || page.path}
                                </p>
                                <BarChart
                                  data={page.daily}
                                  color="bg-indigo-500"
                                  label="views"
                                />
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
}

// ── Emails tab ────────────────────────────────────────────────────────────────

function QuotaBar({ used, limit, color = 'bg-blue-500', label }) {
  const pct = Math.min(Math.round((used / limit) * 100), 100);
  const warn = pct >= 80;
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="text-gray-400">{label}</span>
        <span className={warn ? 'text-amber-400 font-semibold' : 'text-gray-500'}>
          {used} / {limit} ({pct}%)
        </span>
      </div>
      <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${warn ? 'bg-amber-400' : color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function EmailsTab({ token }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchEmailStats(token));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-8">
      <PageHeader title="Email Stats">
        <RefreshBtn onClick={load} loading={loading} />
      </PageHeader>

      {error && <ErrorBanner msg={error} onRetry={load} />}
      {loading && <Loader />}

      {!loading && data && (
        <div className="space-y-8">

          {/* Resend quota cards */}
          <div>
            <SectionLabel>Resend quota (free tier)</SectionLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Today</p>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{data.today_total}</p>
                    <p className="text-xs text-gray-600 mt-0.5">total</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-400">{data.today_otp}</p>
                    <p className="text-xs text-gray-600 mt-0.5">OTP</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-indigo-400">{data.today_invoice}</p>
                    <p className="text-xs text-gray-600 mt-0.5">invoice</p>
                  </div>
                </div>
                <QuotaBar
                  used={data.today_total}
                  limit={data.resend_daily_limit}
                  color="bg-blue-500"
                  label="Daily limit (100/day)"
                />
              </div>

              <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">This month</p>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{data.this_month_total}</p>
                    <p className="text-xs text-gray-600 mt-0.5">total</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-400">{data.this_month_otp}</p>
                    <p className="text-xs text-gray-600 mt-0.5">OTP</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-indigo-400">{data.this_month_invoice}</p>
                    <p className="text-xs text-gray-600 mt-0.5">invoice</p>
                  </div>
                </div>
                <QuotaBar
                  used={data.this_month_total}
                  limit={data.resend_monthly_limit}
                  color="bg-indigo-500"
                  label="Monthly limit (3,000/month)"
                />
              </div>
            </div>
          </div>

          {/* Monthly breakdown table */}
          <div>
            <SectionLabel>Monthly breakdown (last 13 months)</SectionLabel>
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Month</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">OTP</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Invoice</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Total</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-36">Monthly quota</th>
                  </tr>
                </thead>
                <tbody>
                  {[...data.monthly].reverse().map((row, i) => {
                    const pct = Math.min(Math.round((row.total / data.resend_monthly_limit) * 100), 100);
                    const warn = pct >= 80;
                    return (
                      <tr key={row.month}
                        className={`hover:bg-gray-800/40 transition-colors ${i < data.monthly.length - 1 ? 'border-b border-gray-800/60' : ''}`}>
                        <td className="px-5 py-3.5 font-medium text-gray-300">{row.month}</td>
                        <td className="px-5 py-3.5 text-right text-blue-400">{row.otp || '—'}</td>
                        <td className="px-5 py-3.5 text-right text-indigo-400">{row.invoice || '—'}</td>
                        <td className="px-5 py-3.5 text-right font-bold text-white">{row.total || '—'}</td>
                        <td className="px-5 py-3.5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-20 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${warn ? 'bg-amber-400' : 'bg-indigo-500'}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className={`text-xs w-8 text-right ${warn ? 'text-amber-400' : 'text-gray-600'}`}>
                              {pct > 0 ? `${pct}%` : '—'}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      )}
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────

export default function AdminPage() {
  const [token, setToken] = useState(() => getAdminToken());
  const [tab,   setTab]   = useState('overview');

  function handleLogout() {
    clearAdminToken();
    setToken(null);
  }

  return (
    <>
      {/* Prevent all search engine indexing */}
      <Helmet>
        <meta name="robots" content="noindex, nofollow, noarchive, nosnippet" />
      </Helmet>

      {!token ? (
        <AdminLogin onLogin={setToken} />
      ) : (
        <div className="flex h-screen bg-gray-950 overflow-hidden">
          <Sidebar active={tab} onNav={setTab} onLogout={handleLogout} />
          <main className="flex-1 overflow-auto">
            {tab === 'overview'   && <OverviewTab   token={token} />}
            {tab === 'analytics'  && <AnalyticsTab  token={token} />}
            {tab === 'revenue'    && <RevenueTab    token={token} />}
            {tab === 'users'      && <UsersTab      token={token} />}
            {tab === 'activity'   && <ActivityTab   token={token} />}
            {tab === 'emails'     && <EmailsTab     token={token} />}
          </main>
        </div>
      )}
    </>
  );
}
